import React, { useRef, useState } from 'react';

import {
  IConciliacao,
  IContaConciliada,
  IContaDoPlano,
  ISaldoDeConta,
  useFechamentoPendentes,
} from '../../../../../../hooks/useFechamentoContabilMensal';
import { num, numCurto, saldoDC, textoDaContaOu } from '../../regrasDoDetalhe';
import { CABECA_DA_TABELA, CAIXA, LINK, pilula, SUB, TH, TITULO } from './estilos';

type Lado = 'extrato' | 'razao' | 'todos';
type Plano = Record<string, IContaDoPlano> | undefined;

interface IFiltro {
  lado: Lado;
  categoria: string;
  conta: string;
  pagina: number;
}

const LADOS: { chave: Lado; nome: string }[] = [
  { chave: 'extrato', nome: 'No banco, sem lançamento' },
  { chave: 'razao', nome: 'Lançado, sem extrato' },
  { chave: 'todos', nome: 'Todos' },
];

const conciliada = (c: IContaConciliada) =>
  !c.pendentes_qtd && (c.diferenca === 0 || c.diferenca == null);

const TD: React.CSSProperties = { padding: '8px 10px' };

/** Chip de filtro: azul quando ligado. */
function Filtro({
  ligado,
  onClick,
  children,
}: {
  ligado: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ligado}
      style={{
        padding: '4px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        border: `1px solid ${ligado ? '#1B6FD0' : '#D4DAE2'}`,
        background: ligado ? '#E9F4FD' : '#FFFFFF',
        color: ligado ? '#1B6FD0' : '#52525B',
      }}
    >
      {children}
    </button>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, padding: 20, textAlign: 'center', fontSize: 12.5, color: '#52525B' }}>
      {children}
    </p>
  );
}

function SaldosDeContas({ contas, plano }: { contas: ISaldoDeConta[]; plano: Plano }) {
  if (!contas.length) return <Vazio>Nenhuma conta bancária com saldo.</Vazio>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr style={CABECA_DA_TABELA}>
          <th style={TH}>Conta</th>
          <th style={{ ...TH, textAlign: 'right' }}>Saldo contábil</th>
        </tr>
      </thead>
      <tbody>
        {contas.map(c => (
          <tr key={c.conta} style={{ borderTop: '1px solid #E4E4E7' }}>
            <td style={{ ...TD, fontWeight: 600 }}>{textoDaContaOu(c.conta, plano, c.nome)}</td>
            <td style={{ ...TD, textAlign: 'right' }}>{saldoDC(c.saldo)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Duas fontes do mesmo número na mesma escala: contabilidade e extrato. */
function Comparacao({
  titulo,
  contabil,
  extrato,
  formato = num,
  children,
}: {
  titulo: string;
  contabil: number | null | undefined;
  extrato: number | null | undefined;
  formato?: (v: number | null | undefined) => string;
  children?: React.ReactNode;
}) {
  const maior = Math.max(Math.abs(contabil || 0), Math.abs(extrato || 0), 1);
  const barras = [
    { rotulo: 'Contabilidade', valor: contabil, cor: '#1B88F5' },
    { rotulo: 'Extrato', valor: extrato, cor: '#0891B2' },
  ];
  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#52525B' }}>{titulo}</span>
      {barras.map(b => (
        <div key={b.rotulo} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: '34%', fontSize: 11.5, color: '#52525B' }}>{b.rotulo}</span>
          <span
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background: '#EEF2F6',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                height: 6,
                borderRadius: 3,
                width: `${((Math.abs(b.valor || 0) / maior) * 100).toFixed(1)}%`,
                background: b.cor,
              }}
            />
          </span>
          <b style={{ width: 118, textAlign: 'right', fontSize: 12 }}>{formato(b.valor)}</b>
        </div>
      ))}
      {children}
    </div>
  );
}

function ItensPendentes({
  empresaId,
  competencia,
  dados,
  plano,
  filtro,
  setFiltro,
}: {
  empresaId: number;
  competencia: string;
  dados: IConciliacao;
  plano: Plano;
  filtro: IFiltro;
  setFiltro: (f: IFiltro) => void;
}) {
  const consulta = useFechamentoPendentes(empresaId, competencia, {
    lado: filtro.lado === 'todos' ? undefined : filtro.lado,
    categoria: filtro.categoria || undefined,
    conta: filtro.conta || undefined,
    pagina: filtro.pagina,
  });
  const pagina = consulta.data?.dados;
  const qtdDoLado = (c: NonNullable<IConciliacao['pendentes_por_categoria']>[number]) => {
    if (filtro.lado === 'extrato') return c.sem_lancamento;
    if (filtro.lado === 'razao') return c.sem_extrato;
    return c.itens;
  };
  const categorias = (dados.pendentes_por_categoria ?? []).filter(c => qtdDoLado(c) > 0);
  const nomeDaCategoria = (chave: string) =>
    (dados.pendentes_por_categoria ?? []).find(c => c.categoria === chave)?.nome ?? chave;
  const contaDoFiltro = dados.contas.find(c => c.conta === filtro.conta);

  return (
    <section aria-label="Itens pendentes" style={{ ...CAIXA, gap: 10 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={TITULO}>Itens pendentes</div>
          <span style={SUB}>
            Movimento sem par entre o banco e a contabilidade. Escolha o lado e a natureza para
            tratar em lote.
          </span>
        </div>
        {pagina && (
          <span
            style={pilula(
              filtro.lado === 'extrato' ? '#FCF1DD' : '#FEF9C3',
              filtro.lado === 'extrato' ? '#B45309' : '#A16207',
            )}
          >
            {pagina.total} item(ns) · {num(pagina.valor)}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {LADOS.map(l => (
          <Filtro
            key={l.chave}
            ligado={filtro.lado === l.chave}
            onClick={() => setFiltro({ ...filtro, lado: l.chave, categoria: '', pagina: 1 })}
          >
            {l.nome}
          </Filtro>
        ))}
      </div>
      {/* com filtro de conta as contagens por natureza seriam da empresa inteira, não da conta */}
      {!filtro.conta && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Filtro
            ligado={!filtro.categoria}
            onClick={() => setFiltro({ ...filtro, categoria: '', pagina: 1 })}
          >
            Todas as naturezas
          </Filtro>
          {categorias.map(c => (
            <Filtro
              key={c.categoria}
              ligado={filtro.categoria === c.categoria}
              onClick={() => setFiltro({ ...filtro, categoria: c.categoria, pagina: 1 })}
            >
              {c.nome} <b style={{ fontWeight: 800 }}>{qtdDoLado(c)}</b>
            </Filtro>
          ))}
        </div>
      )}
      {filtro.conta && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#52525B' }}>
            Só a conta{' '}
            <b style={{ color: '#0F172A' }}>
              {textoDaContaOu(filtro.conta, plano, contaDoFiltro?.nome)}
            </b>
          </span>
          <button
            type="button"
            style={LINK}
            onClick={() => setFiltro({ ...filtro, conta: '', pagina: 1 })}
          >
            ver todas as contas
          </button>
        </div>
      )}

      {consulta.isLoading && <Vazio>Carregando os itens...</Vazio>}
      {pagina && pagina.itens.length === 0 && <Vazio>Nada pendente com este filtro.</Vazio>}
      {pagina && pagina.itens.length > 0 && (
        <>
          <div
            className="czScroll"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            role="region"
            aria-label="Lista de itens pendentes"
            style={{ overflowX: 'auto', opacity: consulta.isPlaceholderData ? 0.6 : 1 }}
          >
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}
            >
              <thead>
                <tr style={CABECA_DA_TABELA}>
                  <th style={{ ...TH, width: 92 }}>Data</th>
                  <th style={TH}>Conta</th>
                  <th style={TH}>Natureza</th>
                  <th style={TH}>Onde está</th>
                  <th style={TH}>Sentido</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Valor</th>
                  <th style={TH}>Histórico</th>
                </tr>
              </thead>
              <tbody>
                {pagina.itens.map((item, i) => (
                  <tr
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${pagina.pagina}-${i}`}
                    className="linha"
                    style={{ borderTop: '1px solid #E4E4E7' }}
                  >
                    <td style={TD}>{item.data}</td>
                    <td style={{ ...TD, fontSize: 11.5 }}>
                      {textoDaContaOu(item.conta, plano, item.conta_nome)}
                    </td>
                    <td style={{ ...TD, fontSize: 11.5 }}>{nomeDaCategoria(item.categoria)}</td>
                    <td style={{ ...TD, fontSize: 11.5 }}>
                      {item.lado === 'extrato'
                        ? 'no banco, sem lançamento'
                        : 'lançado, sem extrato'}
                    </td>
                    <td style={TD}>{item.sentido === 'E' ? 'entrada' : 'saída'}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{num(item.valor)}</td>
                    <td style={{ ...TD, fontSize: 11.5 }}>{item.historico}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagina.paginas > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Filtro
                ligado={false}
                onClick={() =>
                  pagina.pagina > 1 && setFiltro({ ...filtro, pagina: pagina.pagina - 1 })
                }
              >
                Anterior
              </Filtro>
              <span style={{ fontSize: 12, color: '#52525B' }}>
                Página {pagina.pagina} de {pagina.paginas}
              </span>
              <Filtro
                ligado={false}
                onClick={() =>
                  pagina.pagina < pagina.paginas &&
                  setFiltro({ ...filtro, pagina: pagina.pagina + 1 })
                }
              >
                Próxima
              </Filtro>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ContaDoBanco({
  conta,
  plano,
  onVerItens,
}: {
  conta: IContaConciliada;
  plano: Plano;
  onVerItens: () => void;
}) {
  const ok = conciliada(conta);
  let etiqueta = { texto: 'Conciliada', cor: '#15803D', suave: '#E5F5ED' };
  if (!ok)
    etiqueta = conta.pendentes_qtd
      ? { texto: `${conta.pendentes_qtd} pendência(s)`, cor: '#B45309', suave: '#FCF1DD' }
      : { texto: 'Saldo com diferença', cor: '#A16207', suave: '#FEF9C3' };
  const sub = [
    `Extrato ${conta.extratos.join(', ')}`,
    conta.fonte_saldo,
    conta.reclassificacoes
      ? `${conta.reclassificacoes} reclassificação(ões) de saldo negativo para o passivo`
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const titulo = textoDaContaOu(conta.conta, plano, conta.nome);
  return (
    <section aria-label={titulo} style={{ ...CAIXA, gap: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={TITULO}>{titulo}</div>
          <span style={SUB}>{sub}</span>
        </div>
        <span style={{ ...pilula(etiqueta.suave, etiqueta.cor), flexShrink: 0 }}>
          {etiqueta.texto}
        </span>
      </div>
      {conta.alerta && (
        <div
          style={{
            padding: '10px 12px',
            borderLeft: '3px solid #D97706',
            borderRadius: 8,
            background: 'rgba(241, 245, 249, .9)',
            fontSize: 12,
            color: '#334155',
          }}
        >
          {conta.alerta}
        </div>
      )}
      <div className="cz3">
        <Comparacao
          titulo="Saldo no fim do mês"
          contabil={conta.saldo_contabil}
          extrato={conta.saldo_extrato}
          formato={saldoDC}
        >
          <span style={{ fontSize: 11.5 }}>
            Diferença:{' '}
            <b style={{ color: conta.diferenca ? '#B91C1C' : '#0F172A' }}>
              {conta.diferenca == null ? 'sem saldo contábil' : saldoDC(conta.diferenca)}
            </b>
          </span>
        </Comparacao>
        <Comparacao
          titulo="Entradas do mês"
          contabil={conta.entradas_razao}
          extrato={conta.entradas_extrato}
        />
        <Comparacao
          titulo="Saídas do mês"
          contabil={conta.saidas_razao}
          extrato={conta.saidas_extrato}
        />
      </div>
      {conta.pendentes_qtd > 0 && (
        <button
          type="button"
          onClick={onVerItens}
          style={{ ...LINK, alignSelf: 'flex-start', textDecoration: 'none' }}
        >
          Ver {conta.pendentes_qtd} item(ns) pendente(s)
        </button>
      )}
    </section>
  );
}

function Indicador({
  rotulo,
  valor,
  sub,
  onClick,
}: {
  rotulo: string;
  valor: React.ReactNode;
  sub: string;
  onClick?: () => void;
}) {
  const corpo = (
    <>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: '#52525B',
        }}
      >
        {rotulo}
      </span>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{valor}</span>
      <span style={SUB}>{sub}</span>
    </>
  );
  const estilo: React.CSSProperties = {
    ...CAIXA,
    gap: 4,
    textAlign: 'left',
    color: '#0F172A',
    font: 'inherit',
  };
  return onClick ? (
    <button
      type="button"
      className="ind"
      onClick={onClick}
      style={{ ...estilo, cursor: 'pointer' }}
    >
      {corpo}
    </button>
  ) : (
    <div style={estilo}>{corpo}</div>
  );
}

const COMO_FUNCIONA = (
  <section aria-label="Como funciona" style={{ ...CAIXA, gap: 4 }}>
    <div style={TITULO}>Como funciona</div>
    <span style={SUB}>
      Os extratos importados no ContaCil são comparados com a contabilidade conta a conta. O saldo
      do último dia do mês deve ser igual, e cada movimento do extrato deve ter um lançamento de
      mesmo valor, sentido e data (até 3 dias de diferença). O que sobra são as pendências da
      conciliação.
    </span>
  </section>
);

/** Aba Conciliação bancária: extratos do ContaCil contra a contabilidade, conta a conta. */
export function AbaConciliacao({
  dados,
  plano,
  empresaId,
  competencia,
}: {
  dados: IConciliacao;
  plano: Plano;
  empresaId: number;
  competencia: string;
}) {
  const [filtro, setFiltro] = useState<IFiltro>({
    lado: 'extrato',
    categoria: '',
    conta: '',
    pagina: 1,
  });
  const lista = useRef<HTMLDivElement>(null);
  const irParaLista = (mudanca: Partial<IFiltro>) => {
    setFiltro({ lado: 'todos', categoria: '', conta: '', pagina: 1, ...mudanca });
    lista.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!dados.disponivel) {
    return (
      <>
        {COMO_FUNCIONA}
        <div
          style={{
            padding: '12px 14px',
            borderLeft: '3px solid #DC2626',
            borderRadius: 8,
            background: 'rgba(254, 242, 242, .9)',
            fontSize: 12.5,
            color: '#334155',
          }}
        >
          <b>Sem extratos para esta empresa.</b> A conciliação bancária é um dado essencial do
          procedimento: sem ela o fechamento não pode ser liberado. Importe os extratos da
          competência no ContaCil e rode a revisão novamente.
        </div>
        <section aria-label="Contas bancárias na contabilidade" style={CAIXA}>
          <div style={TITULO}>Contas bancárias na contabilidade</div>
          <span style={SUB}>Saldos contábeis que ficaram sem conferência com o banco.</span>
          <SaldosDeContas contas={dados.contas_bancarias ?? []} plano={plano} />
        </section>
      </>
    );
  }

  const pendentes = dados.contas.reduce((soma, c) => soma + (c.pendentes_qtd || 0), 0);
  const semLancamento = dados.sem_lancamento ?? { itens: 0, valor: 0 };

  return (
    <>
      {COMO_FUNCIONA}
      <div className="kpiGrid4">
        <Indicador
          rotulo="Contas conciliadas"
          valor={`${dados.contas.filter(conciliada).length} de ${dados.contas.length}`}
          sub="saldo e movimento sem diferença"
        />
        <Indicador
          rotulo="Itens pendentes"
          valor={pendentes}
          sub="movimentos sem par entre banco e contabilidade · clique para ver a lista"
          onClick={pendentes > 0 ? () => irParaLista({ lado: 'todos' }) : undefined}
        />
        <Indicador
          rotulo="No banco, sem lançamento"
          valor={semLancamento.itens}
          sub={`${numCurto(semLancamento.valor)} movimentado no extrato e ausente na contabilidade`}
          onClick={pendentes > 0 ? () => irParaLista({ lado: 'extrato' }) : undefined}
        />
        <Indicador
          rotulo="Contas sem extrato"
          valor={dados.sem_extrato.length}
          sub="bancos ou aplicações com saldo ou movimento sem extrato"
        />
      </div>

      {pendentes > 0 && (
        <div ref={lista} style={{ scrollMarginTop: 16 }}>
          <ItensPendentes
            empresaId={empresaId}
            competencia={competencia}
            dados={dados}
            plano={plano}
            filtro={filtro}
            setFiltro={setFiltro}
          />
        </div>
      )}

      {dados.contas.map(conta => (
        <ContaDoBanco
          key={conta.conta}
          conta={conta}
          plano={plano}
          onVerItens={() => irParaLista({ conta: conta.conta })}
        />
      ))}

      {dados.sem_extrato.length > 0 && (
        <section aria-label="Contas sem extrato" style={CAIXA}>
          <div style={TITULO}>Contas sem extrato</div>
          <span style={SUB}>
            Bancos ou aplicações com saldo ou movimento na contabilidade e sem extrato importado.
          </span>
          <SaldosDeContas contas={dados.sem_extrato} plano={plano} />
        </section>
      )}
    </>
  );
}
